import {
  bySubstance,
  kitPartCss,
  pageLabel,
  positive,
  rankMarker,
  rankMarkerCss,
} from '../armKit';

import type { ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;

const FLAG = 'substanceranked';
const UNRANKED = 'unranked';
const METADATA_ONLY = 'Metadata only';

function armCss(scope: string): string {
  return (
    rankMarkerCss(scope, {
      minWidth: '28px',
      fontSize: '20px',
      marginRight: '6px',
      narrowMinWidth: '22px',
    }) + kitPartCss('marker', scope, 'font-weight: 400;')
  );
}

// Zero is how an unknown page count arrives, so it has to read as unknown in all
// three places it is used: the sort key, the chip, and whether a paper can carry
// a rank at all. A rank beside a blank is worse than no rank.
function pagesOf(facts: Facts, workId: number): number | null {
  return positive(facts.get(workId)?.pageCount);
}

function rankable(card: ProfileCard, facts: Facts): boolean {
  return card.hasDownloadable || pagesOf(facts, card.workId) != null;
}

function mark(card: ProfileCard): string | null {
  return card.flag(FLAG);
}

function issued(cards: ProfileCard[]): number {
  return cards.reduce((top, card) => Math.max(top, Number(mark(card)) || 0), 0);
}

function annotate(cards: ProfileCard[], facts: Facts, from: number): void {
  let rank = from;

  cards.forEach((card) => {
    if (mark(card) != null) return;

    const pages = pageLabel(pagesOf(facts, card.workId));
    if (pages != null) card.addChip(pages);
    if (!card.hasDownloadable) card.addChip(METADATA_ONLY);

    if (!rankable(card, facts)) {
      card.setFlag(FLAG, UNRANKED);
      return;
    }

    card.prependToTitle(rankMarker(String(rank)));
    card.setFlag(FLAG, String(rank));
    rank += 1;
  });
}

export const substanceRankedCorpus: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    if (!ctx.cards().some((card) => rankable(card, facts))) return;

    ctx.injectStyles(armCss(ctx.scope));
    ctx.reorderCards(
      bySubstance((workId) => pagesOf(facts, workId)),
      'list',
    );

    // Read back rather than reuse the array above: the ranks run down the
    // reordered DOM, which is the only record of the order.
    annotate(ctx.cards(), facts, 1);
  },

  // Numbered on from the last rank issued and never re-sorted: hoisting a late
  // batch past cards the author is already scrolling costs more than a tail out
  // of order. Gated on the marks onReady left, so a batch cannot engage an arm
  // that degraded to control.
  onCards: async (cards, ctx) => {
    const facts = await ctx.workFacts();
    const ordered = ctx.cards();
    if (!ordered.some((card) => mark(card) != null)) return;

    annotate(cards, facts, issued(ordered) + 1);
  },
};
